import * as React from "react";
import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    Chip,
    Divider,
    Stack,
    Typography,
} from "@mui/material";
import {
    ChangeCircleOutlined,
    ErrorOutline,
    InfoOutlined,
    NoteAddOutlined,
} from "@mui/icons-material";
import { alpha, SxProps, Theme } from "@mui/material/styles";
import { unwrapResult } from "@reduxjs/toolkit";
import { Localized, useLocalization } from "@fluent/react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LpField } from "../../../app/components/LpField";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { selectSettings } from "../../settings/settingsSlice";
import {
    DocumentHeader,
    DocumentRejectionInfo,
    loadDocumentRejectionInfo,
} from "../documentsApi";

type DocumentRejectPanelProps = {
    document: DocumentHeader;
    onSendToCorrection?: () => void;
    onCreateNewVersion: () => void;
    sx?: SxProps<Theme>;
};

export const DocumentRejectPanel: React.FC<DocumentRejectPanelProps> = ({
    document,
    onSendToCorrection,
    onCreateNewVersion,
    sx,
}) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const { locale } = useAppSelector(selectSettings);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [rejectionInfo, setRejectionInfo] =
        useState<DocumentRejectionInfo | null>(null);

    useEffect(() => {
        let alive = true;

        setIsLoading(true);
        setError(null);

        dispatch(loadDocumentRejectionInfo(document.id))
            .then(unwrapResult)
            .then((data) => {
                if (alive) {
                    setRejectionInfo(data ?? null);
                }
            })
            .catch((e) => {
                if (alive) {
                    setError(e);
                }
            })
            .finally(() => {
                if (alive) {
                    setIsLoading(false);
                }
            });

        return () => {
            alive = false;
        };
    }, [document.id, dispatch]);

    if (isLoading) {
        return <LoadingContainer />;
    }

    if (error) {
        return <ErrorContainer error={error} />;
    }

    if (!rejectionInfo?.documentId) {
        return null;
    }

    const rejectedActions =
        rejectionInfo.rejectedActions?.length > 0
            ? rejectionInfo.rejectedActions
            : [rejectionInfo];

    const rejectionCountLabel = l10n.getString(
        "document-rejection-count",
        { count: rejectedActions.length },
        `_${rejectedActions.length} rejections`
    );

    return (
        <Card
            variant="outlined"
            sx={[
                {
                    borderColor: "error.light",
                    borderLeftWidth: 4,
                    overflow: "hidden",
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <Box sx={{ px: 2, py: 1.5 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ minWidth: 0 }}
                    >
                        <ErrorOutline color="error" fontSize="small" />

                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ minWidth: 0 }}
                        >
                            <Localized id="document-rejection-title">
                                <span>_Document was rejected</span>
                            </Localized>
                        </Typography>
                    </Stack>

                    <Chip
                        label={rejectionCountLabel}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ flexShrink: 0 }}
                    />
                </Stack>
            </Box>

            <Divider />

            <Stack divider={<Divider />} sx={{ px: 2 }}>
                {rejectedActions.map((rejectedAction, index) => (
                    <Stack
                        key={`${rejectedAction.rejectedAt ?? "unknown"}-${rejectedAction.rejectedBy}-${index}`}
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                        sx={{ py: 1.5 }}
                    >
                        <LpField
                            value={index + 1}
                            sx={{ mt: 0.1, flexShrink: 0 }}
                        />

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                <Localized id="document-rejection-reason">
                                    <Box
                                        component="span"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        _Reason:
                                    </Box>
                                </Localized>{" "}
                                {rejectedAction.rejectionReason ||
                                    l10n.getString(
                                        "document-rejection-reason-empty",
                                        undefined,
                                        "_No rejection reason was provided."
                                    )}
                            </Typography>

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={{ xs: 0.25, sm: 2 }}
                                sx={{ mt: 0.75 }}
                            >
                                {rejectedAction.rejectedByFullName && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        <Localized id="document-rejection-rejected-by">
                                            <Box
                                                component="span"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                _Rejected by:
                                            </Box>
                                        </Localized>{" "}
                                        {rejectedAction.rejectedByFullName}
                                        {rejectedAction.rejectedBy &&
                                            ` (${rejectedAction.rejectedBy})`}
                                    </Typography>
                                )}

                                {rejectedAction.rejectedAt && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        <Localized id="document-rejection-date">
                                            <Box
                                                component="span"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                _Date:
                                            </Box>
                                        </Localized>{" "}
                                        {new Date(
                                            rejectedAction.rejectedAt
                                        ).toLocaleDateString(locale)}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                ))}
            </Stack>

            {document.status === "R" && (
                <>
                    <Divider />

                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        alignItems={{ xs: "stretch", md: "center" }}
                        justifyContent="space-between"
                        spacing={1.5}
                        sx={{
                            px: 2,
                            py: 1.5,
                            backgroundColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.07),
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                        >
                            <InfoOutlined
                                color="primary"
                                fontSize="small"
                                sx={{ mt: 0.15 }}
                            />

                            <Box>
                                <Typography
                                    variant="body2"
                                    color="primary.main"
                                    fontWeight={700}
                                >
                                    <Localized id="document-rejection-next-step-title">
                                        <span>_What next?</span>
                                    </Localized>
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.15 }}
                                >
                                    <Localized id="document-rejection-next-step-description">
                                        <span>
                                            _Send the document for correction or
                                            create a new version.
                                        </span>
                                    </Localized>
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent={{
                                xs: "flex-end",
                                md: "initial",
                            }}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {onSendToCorrection && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<ChangeCircleOutlined />}
                                    onClick={onSendToCorrection}
                                >
                                    <Localized id="document-action-send-to-correction">
                                        <span>_Send to correction</span>
                                    </Localized>
                                </Button>
                            )}

                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<NoteAddOutlined />}
                                onClick={onCreateNewVersion}
                            >
                                <Localized id="document-new-version">
                                    <span>_New document version</span>
                                </Localized>
                            </Button>
                        </Stack>
                    </Stack>
                </>
            )}
        </Card>
    );
};